/**
 * Reply form values taken from the letter being answered.
 * A thread can hold an older letter. Category, subject, and the specific
 * recipient come from this letter, not from that older one.
 *
 * An incoming letter is answered back to its sender. An outgoing letter is
 * answered again to its original receiver, from our own clinic.
 */

import { officialCprDigits, replacementCprValue } from '@/lib/cpr';

export type ReplyDirection = 'in' | 'out';

export type SpecificRecipientKind = 'person' | 'team';

export type ReplyLetterSource = {
  categoryCode: string | null;
  topic: string | null;
  senderOrganization: string | null;
  senderSor: string | null;
  /** GLN on the organisation that sent the letter. */
  senderGln?: string | null;
  /** CareTeam or person on the sender extension. */
  specificSenderName: string | null;
  /** Resource on the sender extension. A CareTeam is answered as a CareTeam (MedCom 3.3.4.4). */
  specificSenderKind?: 'person' | 'team' | 'organization' | null;
  /** Contact person who wrote the letter. */
  senderPractitionerName: string | null;
  senderRole: string | null;
  /** Original receiver. Used when answering an outgoing letter. */
  receiverOrganization?: string | null;
  receiverSor?: string | null;
  receiverGln?: string | null;
  /** Communication.recipient when that party is not the organisation. */
  specificRecipientName?: string | null;
  specificRecipientRole?: string | null;
  specificRecipientKind?: SpecificRecipientKind | null;
  /** Payload authors, used when MessageHeader sender is our chosen sender. */
  textSegments?: ReadonlyArray<{
    signature?: { name?: string | null; role?: string | null } | null;
  }>;
};

export type ReplyRecipient = {
  family: string;
  given: string[];
  roleText?: string;
};

/**
 * A CareTeam as Communication.recipient (medcom-core-careteam: name 1..1,
 * managingOrganization = the receiver Organization). Sent to communicate-send
 * as `recipientCareTeam`. The contact person is shown in the form only; the
 * MedCom profile does not use CareTeam.participant, so it is not sent.
 */
export type ReplyCareTeam = {
  name: string;
};

type SpecificRecipient = {
  label: string;
  recipient: ReplyRecipient | null;
  careTeam: ReplyCareTeam | null;
};

export type ReplyLetterFields = {
  categoryCode: string | null;
  topic: string;
  receiverName: string;
  receiverSor: string;
  receiverEan: string;
  specificRecipientLabel: string;
  recipient: ReplyRecipient | null;
  recipientCareTeam: ReplyCareTeam | null;
};

export type ComposeReceiver = {
  sorId: string;
  name: string;
  ean: string;
};

export type PatientIdKind = 'cpr' | 'xEcpr' | 'dEcpr';

/** Patient on the opened letter, or the same patient on the assembled thread. */
export type ReplyPatientSource = {
  cpr?: string | null;
  xEcpr?: string | null;
  dEcpr?: string | null;
  family?: string | null;
  given?: readonly string[] | null;
  /** Used when family and given are not stored separately. */
  patientName?: string | null;
};

export type ReplyPatientIdentifiers = {
  cpr?: string;
  xEcpr?: string;
  dEcpr?: string;
};

export type ListedPatientIdentifier = {
  kind: PatientIdKind;
  value: string;
};

/** Every identifier that a reply or forward copies from the letter, in send order. */
export function listedPatientIdentifiers(
  identifiers: ReplyPatientIdentifiers,
): ListedPatientIdentifier[] {
  const rows: ListedPatientIdentifier[] = [];
  if (identifiers.cpr) rows.push({ kind: 'cpr', value: identifiers.cpr });
  if (identifiers.xEcpr) rows.push({ kind: 'xEcpr', value: identifiers.xEcpr });
  if (identifiers.dEcpr) rows.push({ kind: 'dEcpr', value: identifiers.dEcpr });
  return rows;
}

export type ReplyPatientFields = {
  idKind: PatientIdKind;
  idValue: string;
  family: string;
  given: string;
  /** Every identifier from the source. The sent reply keeps all of them. */
  identifiers: ReplyPatientIdentifiers;
};

export function replyLetterFields(
  letter: ReplyLetterSource,
  direction: ReplyDirection = 'in',
  chosenSenderSor?: string | null,
): ReplyLetterFields {
  switch (direction) {
    case 'out':
      return {
        categoryCode: blankToNull(letter.categoryCode),
        topic: letter.topic?.trim() ?? '',
        receiverName: letter.receiverOrganization?.trim() ?? '',
        receiverSor: letter.receiverSor?.trim() ?? '',
        receiverEan: letter.receiverGln?.trim() ?? '',
        ...outgoingSpecificRecipient(letter),
      };
    case 'in': {
      if (sameSor(letter.senderSor, chosenSenderSor)) {
        const specific = otherPartyRecipient(letter);
        return {
          categoryCode: blankToNull(letter.categoryCode),
          topic: letter.topic?.trim() ?? '',
          receiverName: letter.receiverOrganization?.trim() ?? '',
          receiverSor: letter.receiverSor?.trim() ?? '',
          receiverEan: letter.receiverGln?.trim() ?? '',
          specificRecipientLabel: specific?.label ?? '',
          recipient: specific?.recipient ?? null,
          recipientCareTeam: specific?.careTeam ?? null,
        };
      }
      const specific = incomingSpecificRecipient(letter);
      return {
        categoryCode: blankToNull(letter.categoryCode),
        topic: letter.topic?.trim() ?? '',
        receiverName: letter.senderOrganization?.trim() ?? '',
        receiverSor: letter.senderSor?.trim() ?? '',
        receiverEan: letter.senderGln?.trim() ?? '',
        specificRecipientLabel: specific?.label ?? '',
        recipient: specific?.recipient ?? null,
        recipientCareTeam: specific?.careTeam ?? null,
      };
    }
    default: {
      const unexpected: never = direction;
      return unexpected;
    }
  }
}

/**
 * Receiver on Svar is the other MessageHeader party, never the chosen sender
 * organisation. Incoming: MessageHeader sender, unless that SOR is the chosen
 * sender, in which case MessageHeader receiver is used. Outgoing: original
 * receiver. Videresend leaves the receiver empty for the user.
 */
export function composeReceiver(
  mode: 'reply' | 'forward',
  letter: ReplyLetterSource,
  direction: ReplyDirection,
  chosenSenderSor?: string | null,
): ComposeReceiver | null {
  switch (mode) {
    case 'forward':
      return null;
    case 'reply': {
      const fields = replyLetterFields(letter, direction, chosenSenderSor);
      return {
        sorId: fields.receiverSor,
        name: fields.receiverName,
        ean: fields.receiverEan,
      };
    }
    default: {
      const unexpected: never = mode;
      return unexpected;
    }
  }
}

function otherPartyRecipient(letter: ReplyLetterSource): SpecificRecipient | null {
  const ours = new Set(
    [letter.senderPractitionerName, letter.specificSenderName]
      .map((value) => value?.trim() ?? '')
      .filter((value) => value.length > 0),
  );
  for (const segment of letter.textSegments ?? []) {
    const name = segment.signature?.name?.trim() ?? '';
    if (!name || ours.has(name)) continue;
    const role = blankToNull(segment.signature?.role);
    return { label: name, recipient: personRecipient(name, role), careTeam: null };
  }
  const named = blankToNull(letter.specificRecipientName);
  if (named && !ours.has(named)) {
    if (letter.specificRecipientKind === 'team') {
      return { label: named, recipient: null, careTeam: { name: named } };
    }
    return {
      label: named,
      recipient: personRecipient(named, blankToNull(letter.specificRecipientRole)),
      careTeam: null,
    };
  }
  return null;
}

function sameSor(
  left: string | null | undefined,
  right: string | null | undefined,
): boolean {
  const a = left?.trim() ?? '';
  const b = right?.trim() ?? '';
  return Boolean(a && b && a === b);
}

/**
 * Svar to a received letter: the specific recipient is the letter's specific
 * sender. A CareTeam sender is answered as that CareTeam, not as its contact
 * person (MedCom CC-Send 3.3.4.4). A person sender is answered as that person.
 */
function incomingSpecificRecipient(letter: ReplyLetterSource): SpecificRecipient | null {
  const teamOrPerson = blankToNull(letter.specificSenderName);
  const contact = blankToNull(letter.senderPractitionerName);
  const role = blankToNull(letter.senderRole);
  if (!teamOrPerson && !contact) return null;

  const kind = letter.specificSenderKind ?? null;
  const named = teamOrPerson && teamOrPerson !== contact ? teamOrPerson : null;
  const label =
    named && contact ? `${named} (kontaktperson ${contact})` : (named ?? contact ?? '');
  const isCareTeam = Boolean(named) && (kind === 'team' || kind === null);
  if (isCareTeam && named) {
    return { label, recipient: null, careTeam: { name: named } };
  }
  if (kind === 'team' && teamOrPerson) {
    return { label, recipient: null, careTeam: { name: teamOrPerson } };
  }
  return {
    label,
    recipient: contact ? personRecipient(contact, role) : null,
    careTeam: null,
  };
}

/** The letter's own recipient. The author who wrote our outgoing letter is not one. */
function outgoingSpecificRecipient(letter: ReplyLetterSource): {
  specificRecipientLabel: string;
  recipient: ReplyRecipient | null;
  recipientCareTeam: ReplyCareTeam | null;
} {
  const name = blankToNull(letter.specificRecipientName);
  if (!name) return { specificRecipientLabel: '', recipient: null, recipientCareTeam: null };

  const role = blankToNull(letter.specificRecipientRole);
  const kind = letter.specificRecipientKind ?? null;
  switch (kind) {
    case 'person':
      return {
        specificRecipientLabel: name,
        recipient: personRecipient(name, role),
        recipientCareTeam: null,
      };
    case 'team':
      return { specificRecipientLabel: name, recipient: null, recipientCareTeam: { name } };
    case null:
      return {
        specificRecipientLabel: name,
        recipient: role ? personRecipient(name, role) : null,
        recipientCareTeam: null,
      };
    default: {
      const unexpected: never = kind;
      return unexpected;
    }
  }
}

/**
 * The communicate-send fields for the reply's specific recipient. A CareTeam
 * goes as `recipientCareTeam` (never as its contact person); a person as
 * `recipient`. Nothing is sent when the field was cleared.
 */
export function specificRecipientBody(input: {
  label: string;
  recipient: ReplyRecipient | null;
  careTeam: ReplyCareTeam | null;
}): { recipient: ReplyRecipient } | { recipientCareTeam: ReplyCareTeam } | Record<string, never> {
  if (!input.label.trim()) return {};
  if (input.careTeam?.name.trim()) return { recipientCareTeam: { name: input.careTeam.name.trim() } };
  if (input.recipient) return { recipient: input.recipient };
  return {};
}

function personRecipient(name: string, role: string | null): ReplyRecipient {
  const parts = name.split(/\s+/).filter((part) => part.length > 0);
  const family = parts[parts.length - 1] ?? name;
  const given = parts.length > 1 ? parts.slice(0, -1) : [family];
  return role ? { family, given, roleText: role } : { family, given };
}

function blankToNull(value: string | null | undefined): string | null {
  const trimmed = value?.trim() ?? '';
  return trimmed.length > 0 ? trimmed : null;
}

/**
 * CPR or replacement CPR, plus the name, taken from the letter being answered.
 * A replacement CPR is shown when that is the identifier on the letter.
 * When the letter has more than one identifier, all of them stay on `identifiers`.
 * CPR is only filled from an official ten-digit value (OID 1.2.208.176.1.2).
 */
export function replyPatientFields(
  source: ReplyPatientSource | null | undefined,
): ReplyPatientFields | null {
  if (!source) return null;

  const cpr = officialCprDigits(source.cpr);
  const xEcpr = blankToNull(source.xEcpr);
  const dEcpr = blankToNull(source.dEcpr);
  const identifiers: ReplyPatientIdentifiers = {};
  if (cpr) identifiers.cpr = cpr;
  if (xEcpr) identifiers.xEcpr = xEcpr;
  if (dEcpr) identifiers.dEcpr = dEcpr;

  const idKind = visiblePatientIdKind(identifiers);
  const { family, given } = patientNameParts(source);
  if (!idKind && !family && !given) return null;

  return {
    idKind: idKind ?? 'cpr',
    idValue: idKind ? (identifiers[idKind] ?? '') : '',
    family,
    given,
    identifiers,
  };
}

/** Letter identifiers and name win. The assembled thread fills anything the letter omits. */
export function replyPatientSource(input: {
  letter?: Pick<
    ReplyPatientSource,
    'cpr' | 'xEcpr' | 'dEcpr' | 'family' | 'given' | 'patientName'
  > | null;
  assembled?: Pick<ReplyPatientSource, 'cpr' | 'xEcpr' | 'dEcpr' | 'family' | 'given'> | null;
}): ReplyPatientSource | null {
  const { letter, assembled } = input;
  if (!letter && !assembled) return null;
  const letterGiven = (letter?.given ?? []).filter((part) => part.trim().length > 0);
  const letterXEcpr = blankToNull(letter?.xEcpr);
  const letterDEcpr = blankToNull(letter?.dEcpr);
  const letterOfficialCpr = officialCprDigits(letter?.cpr);
  const letterHasReplacement = Boolean(letterXEcpr || letterDEcpr);
  return {
    cpr: letterOfficialCpr ?? (letterHasReplacement ? null : officialCprDigits(assembled?.cpr)),
    xEcpr: letterXEcpr ?? assembled?.xEcpr,
    dEcpr: letterDEcpr ?? assembled?.dEcpr,
    family: blankToNull(letter?.family) ?? assembled?.family,
    given: letterGiven.length > 0 ? letterGiven : assembled?.given,
    patientName: letter?.patientName,
  };
}

/** Identifier typed in a new letter, where the form has one field. */
export function patientIdentifiersFromField(
  kind: PatientIdKind,
  value: string,
): ReplyPatientIdentifiers {
  const trimmed = value.trim();
  if (!trimmed) return {};
  switch (kind) {
    case 'cpr': {
      const digits = officialCprDigits(trimmed);
      return digits ? { cpr: digits } : {};
    }
    case 'xEcpr':
      return { xEcpr: trimmed };
    case 'dEcpr':
      return { dEcpr: trimmed };
    default: {
      const unexpected: never = kind;
      return unexpected;
    }
  }
}

function firstReplacementIdentifier(
  listed: ListedPatientIdentifier[],
): { kind: PatientIdKind; value: string } {
  for (const row of listed) {
    switch (row.kind) {
      case 'xEcpr':
      case 'dEcpr':
        return { kind: row.kind, value: row.value };
      case 'cpr':
        break;
      default: {
        const unexpected: never = row.kind;
        return unexpected;
      }
    }
  }
  return { kind: 'xEcpr', value: '' };
}

function visiblePatientIdKind(identifiers: ReplyPatientIdentifiers): PatientIdKind | null {
  if (identifiers.xEcpr) return 'xEcpr';
  if (identifiers.dEcpr) return 'dEcpr';
  if (identifiers.cpr) return 'cpr';
  return null;
}

function patientNameParts(source: ReplyPatientSource): { family: string; given: string } {
  const family = blankToNull(source.family);
  const given = (source.given ?? [])
    .map((part) => part.trim())
    .filter((part) => part.length > 0)
    .join(' ');
  if (family && given) return { family, given };

  const name = blankToNull(source.patientName);
  if (name) {
    const parts = name.split(/\s+/).filter((part) => part.length > 0);
    if (parts.length <= 1) {
      return { family: family ?? parts[0] ?? '', given };
    }
    return {
      family: family ?? parts[parts.length - 1] ?? '',
      given: given || parts.slice(0, -1).join(' '),
    };
  }

  return { family: family ?? '', given };
}

/**
 * Identifiers for a new letter opened from the chart. Official CPR is only the
 * decrypted ten-digit value or a letter Patient.identifier with the official
 * CPR system. Replacement identifiers keep the letter's X/D system and value.
 * A decrypted replacement with no system is omitted — the chart does not store
 * X versus D.
 */
export function newLetterPatientIdentifiers(input: {
  decryptedCpr?: string | null;
  letters?: Array<{
    cpr?: string | null;
    xEcpr?: string | null;
    dEcpr?: string | null;
  }>;
}): ReplyPatientIdentifiers {
  const officialFromDecrypt = officialCprDigits(input.decryptedCpr);
  const identifiers: ReplyPatientIdentifiers = {};
  for (const letter of input.letters ?? []) {
    const cpr = officialCprDigits(letter.cpr);
    const xEcpr = blankToNull(letter.xEcpr);
    const dEcpr = blankToNull(letter.dEcpr);
    if (cpr) identifiers.cpr = cpr;
    if (xEcpr) identifiers.xEcpr = xEcpr;
    if (dEcpr) identifiers.dEcpr = dEcpr;
  }
  if (officialFromDecrypt || identifiers.cpr) {
    return { cpr: officialFromDecrypt ?? identifiers.cpr };
  }
  if (
    replacementCprValue(input.decryptedCpr) &&
    !identifiers.xEcpr &&
    !identifiers.dEcpr
  ) {
    return {};
  }
  return identifiers;
}

/**
 * Replacement identifiers on a new letter are shown read-only, like a reply.
 * Official CPR stays an editable CPR field.
 */
export function newLetterPatientComposeState(
  identifiers: ReplyPatientIdentifiers,
): {
  identifiers: ReplyPatientIdentifiers;
  locked: boolean;
  idKind: PatientIdKind;
  idValue: string;
} {
  const listed = listedPatientIdentifiers(identifiers);
  if (identifiers.xEcpr || identifiers.dEcpr) {
    const first = firstReplacementIdentifier(listed);
    return {
      identifiers,
      locked: true,
      idKind: first.kind,
      idValue: first.value,
    };
  }
  const cpr = identifiers.cpr;
  return {
    identifiers: cpr ? { cpr } : {},
    locked: false,
    idKind: 'cpr',
    idValue: cpr ?? '',
  };
}

/**
 * Official CPR for a reply or forward. Only a Patient.identifier with system
 * urn:oid:1.2.208.176.1.2 (already parsed onto `letterCpr`) is used. A decrypted
 * Clinio value is rejected when it is a replacement CPR, and is not used when
 * the letter has no official CPR identifier. The value is not logged.
 */
export function replyPatientCpr(input: {
  letterCpr?: string | null;
  decryptedCpr?: string | null;
  letterXEcpr?: string | null;
  letterDEcpr?: string | null;
}): string | null {
  const fromLetter = officialCprDigits(input.letterCpr);
  if (fromLetter) return fromLetter;
  if (blankToNull(input.letterXEcpr) || blankToNull(input.letterDEcpr)) return null;
  if (input.letterCpr !== undefined) return null;
  return officialCprDigits(input.decryptedCpr);
}

export type PatientGivenFallbackLetter = {
  direction?: 'in' | 'out' | null;
  family?: string | null;
  given?: readonly string[] | null;
};

function sameFamily(a: string | null | undefined, b: string | null | undefined): boolean {
  const left = a?.trim().toLocaleLowerCase('da-DK') ?? '';
  const right = b?.trim().toLocaleLowerCase('da-DK') ?? '';
  return left.length > 0 && left === right;
}

/**
 * Fornavn for a reply or forward when the opened letter has none (MedCom
 * allows a Patient with only a family name). Takes the given name of another
 * letter in the same thread for the same patient, received letters first,
 * then the Clinio patient that the import linked to the letter.
 */
export function fallbackPatientGiven(input: {
  family: string | null | undefined;
  letters?: readonly PatientGivenFallbackLetter[] | null;
  linkedPatient?: { firstName?: string | null; lastName?: string | null } | null;
}): string {
  const family = input.family?.trim() ?? '';
  if (!family) return '';
  const letters = (input.letters ?? []).filter((item) => sameFamily(item.family, family));
  const ordered = [
    ...letters.filter((item) => item.direction !== 'out'),
    ...letters.filter((item) => item.direction === 'out'),
  ];
  for (const item of ordered) {
    const given = (item.given ?? []).map((part) => part.trim()).filter(Boolean).join(' ');
    if (given) return given;
  }
  const linked = input.linkedPatient;
  if (linked && sameFamily(linked.lastName, family)) {
    return linked.firstName?.trim() ?? '';
  }
  return '';
}
